import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { knowledgeProvidersService, type TestSearchHit } from "@/services/knowledgeProvidersService";
import type { KnowledgeQdrantCollection } from "@/types/knowledgeProviders";

interface Props {
  collection: KnowledgeQdrantCollection;
  organizationId?: string;
}

export default function CollectionTestSearch({ collection, organizationId }: Props) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<TestSearchHit[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!organizationId || !query.trim()) return;
    setBusy(true);
    try {
      const res = await knowledgeProvidersService.testSearch(organizationId, {
        collection: collection.collection,
        vectorName: collection.vector_name,
        embeddingModel: collection.embedding_model,
        query,
        limit: collection.max_results,
        scoreThreshold: collection.score_threshold,
        returnedFields: collection.returned_fields,
        rerank: collection.rerank,
        rerankModel: collection.rerank_model,
      });
      setHits(res.results);
      setNote(res.note ?? null);
    } catch (e) {
      setHits(null);
      toast({
        title: "Test search failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={query}
          placeholder="Try a search"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              run();
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" disabled={busy || !query.trim()} onClick={run}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      {note && <p className="text-xs text-amber-600">{note}</p>}
      {hits && hits.length === 0 && <p className="text-sm text-muted-foreground">No results.</p>}
      {hits?.map((hit, i) => (
        <div key={i} className="rounded-md border p-2 text-xs">
          <div className="font-medium">score {hit.score.toFixed(3)}</div>
          <pre className="whitespace-pre-wrap break-words text-muted-foreground">
            {JSON.stringify(hit.payload, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
}
